/**
 * Copy estático centralizado da Landing Page.
 * Fonte: docs/LANDING_REBUILD.md §7.3, §7.1.1, §7.4
 */

import {
  PLAN_LIMITS,
  PLAN_IDS,
  STORAGE_IMAGE_ESTIMATE_NOTE,
} from "@/config/planLimits";
import { CONTACT_EMAIL } from "@/config/billing";

export const LANDING_FEATURES_SECTION = {
  title: "Tudo para transformar seu projeto em uma experiência",
  subtitle:
    "Apresente ambientes, destaque detalhes e compartilhe seu trabalho de uma forma muito mais envolvente.",
};

export const LANDING_FEATURES = [
  {
    icon: "Orbit",
    title: "Experiência 360° imersiva",
    description:
      "Permita que seus clientes explorem cada ambiente com liberdade e em todos os detalhes.",
  },
  {
    icon: "MapPin",
    title: "Hotspots interativos",
    description:
      "Destaque materiais, soluções e informações importantes diretamente no ambiente.",
  },
  {
    icon: "Upload",
    title: "Navegação entre ambientes",
    description: "Conecte suas imagens 360° e transforme o projeto em uma experiência contínua.",
  },
  {
    icon: "Share2",
    title: "Compartilhe com um link",
    description: "Envie o projeto para seus clientes de forma simples, sem arquivos pesados ou instalações.",
  },
  {
    icon: "Briefcase",
    title: "Seu portfólio online",
    description:
      "Reúna seus melhores projetos em uma página profissional pronta para compartilhar.",
  },
  {
    icon: "Eye",
    title: "Você decide quem pode ver",
    description: "Mantenha projetos privados, compartilhe apenas por link ou publique no seu portfólio.",
  },
  {
    icon: "FolderKanban",
    title: "Projetos sempre organizados",
    description:
      "Reúna imagens, informações e ambientes em um único lugar.",
  },
  {
    icon: "LayoutDashboard",
    title: "Acompanhe seu trabalho",
    description: "Visualize projetos, imagens, acessos e uso da sua conta em um dashboard completo.",
  },
  {
    icon: "Code2",
    title: "Leve o 360° para seu site",
    description:
      "Incorpore seus projetos ao seu próprio site e ofereça uma experiência ainda mais completa.",
  },
];

export const LANDING_SHOWCASE = {
  title: "Veja como seu cliente vai explorar seu projeto",
  subtitle:
    "Navegue pelos ambientes, descubra informações nos hotspots e experimente o FIVI360 como seu cliente receberia.",
  fallbackMessage: "Demonstração temporariamente indisponível.",
  openProjectLabel: "Abrir projeto demo",
  portfolioLabel: "Ver portfólio FIVI360",
};

export const LANDING_HOW_IT_WORKS_SECTION = {
  title: "Como funciona",
  subtitle: "Do primeiro panorama ao projeto pronto para compartilhar.",
};

export const LANDING_HOW_IT_WORKS_STEPS = [
  {
    title: "Crie seu projeto",
    description: "Organize seus ambientes e personalize a apresentação do projeto.",
  },
  {
    title: "Adicione suas imagens",
    description: "Envie suas imagens 360° e organize cada ambiente em poucos minutos.",
  },
  {
    title: "Dê vida à experiência",
    description: "Adicione informações e conecte os ambientes com hotspots interativos.",
  },
  {
    title: "Compartilhe com seus clientes",
    description: "Envie um único link ou publique o projeto diretamente no seu portfólio.",
  },
];

export const LANDING_PORTFOLIO = {
  title: "Um portfólio à altura dos seus projetos",
  description:
    "Reúna seus melhores trabalhos em uma página profissional, escolha o que deseja publicar e compartilhe seu portfólio com clientes em um único link.",
  bullets: [
    "Seu endereço personalizado: fivi360.com.br/u/seu-nome",
    "Uma experiência pensada para seus clientes: sem menus administrativos ou distrações.",
    "Projetos prontos para compartilhar: Compartilhe seu portfólio, projetos ou imagens individualmente.",
  ],
  portfolioCtaLabel: "Ver portfólio real",
  registerCtaLabel: "Criar meu portfólio",
};

export const LANDING_PRICING_SECTION = {
  title: "Planos e preços",
  subtitle: "Comece gratuitamente e escolha o plano ideal para a forma como você apresenta seus projetos.",
  footnote:
    "Cobrança online via Stripe. Limites do plano Starter já estão ativos.",
  estimateNote: STORAGE_IMAGE_ESTIMATE_NOTE,
};

/** Bullets de marketing por plano (preços e bullets derivados de PLAN_LIMITS). */
export const LANDING_PRICING_MARKETING = {
  [PLAN_IDS.STARTER]: {
    headline: PLAN_LIMITS[PLAN_IDS.STARTER].tagline,
    description: PLAN_LIMITS[PLAN_IDS.STARTER].description,
    priceLabel: PLAN_LIMITS[PLAN_IDS.STARTER].priceLabel,
    periodLabel: PLAN_LIMITS[PLAN_IDS.STARTER].periodLabel ?? "",
    featureBullets: PLAN_LIMITS[PLAN_IDS.STARTER].featureBullets,
    ctaLabel: "Começar gratuitamente",
    ctaTo: "/register",
    ctaDisabled: false,
    highlighted: false,
    badge: PLAN_LIMITS[PLAN_IDS.STARTER].badge,
  },
  [PLAN_IDS.PROFESSIONAL]: {
    headline: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].tagline,
    description: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].description,
    priceLabel: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].priceLabel,
    periodLabel: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].periodLabel ?? "",
    featureBullets: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].featureBullets,
    ctaLabel: "Assinar Professional",
    ctaTo: "/register",
    highlighted: true,
    badge: PLAN_LIMITS[PLAN_IDS.PROFESSIONAL].badge,
  },
  [PLAN_IDS.STUDIO]: {
    headline: PLAN_LIMITS[PLAN_IDS.STUDIO].tagline,
    description: PLAN_LIMITS[PLAN_IDS.STUDIO].description,
    priceLabel: PLAN_LIMITS[PLAN_IDS.STUDIO].priceLabel,
    periodLabel: PLAN_LIMITS[PLAN_IDS.STUDIO].periodLabel ?? "",
    featureBullets: PLAN_LIMITS[PLAN_IDS.STUDIO].featureBullets,
    ctaLabel: "Assinar Studio",
    ctaTo: "/register",
    highlighted: false,
    badge: PLAN_LIMITS[PLAN_IDS.STUDIO].badge,
  },
  [PLAN_IDS.ENTERPRISE]: {
    headline: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].tagline,
    description: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].description,
    priceLabel: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].priceLabel,
    periodLabel: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].periodLabel ?? "",
    featureBullets: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].featureBullets,
    ctaLabel: "Fale conosco",
    ctaTo: `mailto:${CONTACT_EMAIL}?subject=Plano%20Enterprise%20FIVI360`,
    ctaDisabled: true,
    highlighted: false,
    badge: PLAN_LIMITS[PLAN_IDS.ENTERPRISE].badge,
  },
};

export const LANDING_FAQ_SECTION = {
  title: "Perguntas frequentes",
  subtitle: "Tire suas dúvidas antes de criar sua conta.",
};

export const LANDING_FAQ_ITEMS = [
  {
    question: "O que é uma imagem panorâmica 360°?",
    answer:
      "É um formato equirectangular que permite olhar em todas as direções, como se você estivesse dentro do ambiente.",
  },
  {
    question: "Preciso de equipamento especial?",
    answer:
      "Você pode usar uma câmera 360° ou exportar renders equirectangulares de softwares de visualização e arquitetura.",
  },
  {
    question: "O plano gratuito tem limites?",
    answer:
      "Sim: 2 projetos e 25 MB de armazenamento (aproximadamente 5 imagens panorâmicas). Consulte a tabela de planos acima para comparar.",
  },
  {
    question: "O que são hotspots?",
    answer:
      "São marcadores no viewer com texto informativo ou link para outra cena. Disponíveis nos planos Professional e Studio.",
  },
  {
    question: "Meus clientes precisam de conta?",
    answer:
      "Não. Links compartilhados e o portfólio público são acessíveis sem login.",
  },
  {
    question: "Posso substituir uma imagem?",
    answer:
      "Sim. Você pode trocar o arquivo sem perder nome, descrição ou hotspots já configurados.",
  },
  {
    question: "Posso compartilhar apenas uma imagem?",
    answer:
      "Sim. Cada panorama pode ter um link público próprio, independente do projeto ou do portfólio.",
  },
  {
    question: "O FIVI360 funciona no celular?",
    answer:
      "Sim. O viewer é otimizado para mobile, com controles adaptados à tela touch.",
  },
  {
    question: "Como funciona o portfólio público?",
    answer:
      "Ative em Configurações, defina seu slug personalizado e marque os projetos que deseja exibir publicamente.",
  },
];

export const LANDING_FINAL_CTA = {
  title: "Pronto para impressionar seus clientes?",
  description:
    "Crie sua conta gratuita e publique seu primeiro projeto hoje.",
  buttonLabel: "Começar Gratuitamente",
  footnote: "Sem cartão de crédito.",
};
